/**
 * PayPalButton Component
 *
 * Wrapper component for PayPal Buttons SDK integration.
 * Handles order creation, payment approval, cancellation, and errors.
 *
 * Features:
 * - Configures button style (vertical, white, pill, pay label)
 * - Sets up experience context (brand_name, locale, user_action)
 * - Creates orders with 4.99 EUR amount
 * - Captures payments and sends to backend for verification
 * - Handles cancellation and error states
 * - Supports enabled/disabled state based on terms acceptance
 *
 * @validates Requirements 1.1, 1.2, 1.4, 1.5, 2.1, 2.2, 2.3, 3.1, 3.2
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  PAYPAL_BUTTON_STYLE,
  PAYPAL_EXPERIENCE,
} from '../../hooks/usePayPalScript';
import { useStore } from '../../store';

/**
 * PayPal order data returned after successful capture
 */
export interface PayPalOrderData {
  orderId: string;
  captureId: string;
  amount: string;
  payerEmail?: string;
  payerName?: string;
}

/**
 * Props for the PayPalButton component
 */
export interface PayPalButtonProps {
  /** User ID to link the payment to */
  userId: string;
  /** Callback when payment is successfully captured */
  onSuccess: (orderData: PayPalOrderData) => void;
  /** Callback when user cancels the payment */
  onCancel: () => void;
  /** Callback when an error occurs */
  onError: (error: Error) => void;
  /** Whether the button is enabled (terms accepted) */
  enabled: boolean;
}

/**
 * Type for the PayPal global object
 */
interface PayPalNamespace {
  Buttons: (config: PayPalButtonsConfig) => PayPalButtonsInstance;
}

/**
 * PayPal Buttons configuration interface
 */
interface PayPalButtonsConfig {
  style?: {
    layout?: 'vertical' | 'horizontal';
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
    shape?: 'rect' | 'pill';
    label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment';
  };
  createOrder: (
    data: Record<string, unknown>,
    actions: PayPalOrderActions
  ) => Promise<string>;
  onApprove: (
    data: PayPalApproveData,
    actions: PayPalOrderActions
  ) => Promise<void>;
  onCancel?: (data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
}

/**
 * PayPal order actions
 */
interface PayPalOrderActions {
  order: {
    create: (orderData: PayPalOrderCreateData) => Promise<string>;
    capture: () => Promise<PayPalCaptureDetails>;
  };
}

/**
 * PayPal order create data
 */
interface PayPalOrderCreateData {
  purchase_units: Array<{
    amount: {
      value: string;
      currency_code: string;
    };
    reference_id?: string;
  }>;
  application_context?: {
    brand_name?: string;
    user_action?: string;
    locale?: string;
  };
}

/**
 * PayPal approve callback data
 */
interface PayPalApproveData {
  orderID: string;
  payerID?: string;
}

/**
 * PayPal capture details
 */
interface PayPalCaptureDetails {
  id: string;
  status: string;
  payer?: {
    email_address?: string;
    name?: {
      given_name?: string;
      surname?: string;
    };
  };
  purchase_units?: Array<{
    payments?: {
      captures?: Array<{
        id: string;
        amount?: {
          value: string;
          currency_code: string;
        };
      }>;
    };
  }>;
}

/**
 * PayPal Buttons instance
 */
interface PayPalButtonsInstance {
  render: (container: HTMLElement | string) => Promise<void>;
  close: () => void;
}

/**
 * Get the PayPal global namespace
 */
function getPayPal(): PayPalNamespace | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).paypal || null;
}

/**
 * PayPalButton Component
 *
 * Renders PayPal payment buttons when enabled and the SDK is loaded.
 * The button is hidden when disabled (terms not accepted).
 *
 * @example
 * ```tsx
 * <PayPalButton
 *   userId={user.id}
 *   onSuccess={(orderData) => handlePaymentSuccess(orderData)}
 *   onCancel={() => setPaymentCancelled(true)}
 *   onError={(error) => setPaymentError(error)}
 *   enabled={termsAccepted}
 * />
 * ```
 */
export function PayPalButton({
  userId,
  onSuccess,
  onCancel,
  onError,
  enabled,
}: PayPalButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Get configurable price from store
  const premiumMonthlyPrice = useStore((state) => state.premiumMonthlyPrice);
  const premiumPriceString = premiumMonthlyPrice.toFixed(2);

  /**
   * Create PayPal order with the Premium subscription amount
   * @validates Requirement 2.1, 2.2
   */
  const createOrder = useCallback(
    async (
      _data: Record<string, unknown>,
      actions: PayPalOrderActions
    ): Promise<string> => {
      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: premiumPriceString,
              currency_code: 'EUR',
            },
            reference_id: userId,
          },
        ],
        application_context: {
          brand_name: PAYPAL_EXPERIENCE.brand_name,
          user_action: PAYPAL_EXPERIENCE.user_action,
          locale: PAYPAL_EXPERIENCE.locale,
        },
      });
    },
    [userId, premiumPriceString]
  );

  /**
   * Handle successful payment approval
   * Captures the order and calls onSuccess with order details
   * @validates Requirement 3.1, 3.2
   */
  const handleApprove = useCallback(
    async (
      data: PayPalApproveData,
      actions: PayPalOrderActions
    ): Promise<void> => {
      try {
        const details = await actions.order.capture();

        // Extract capture ID from the response
        const captureId =
          details.purchase_units?.[0]?.payments?.captures?.[0]?.id || details.id;

        // Extract payer information if available
        const payerEmail = details.payer?.email_address;
        const payerName = details.payer?.name
          ? `${details.payer.name.given_name || ''} ${details.payer.name.surname || ''}`.trim()
          : undefined;

        // Get the actual amount from the capture details
        const capturedAmount = 
          details.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value || premiumPriceString;

        // Call success callback with order data
        onSuccess({
          orderId: data.orderID,
          captureId,
          amount: capturedAmount,
          payerEmail,
          payerName,
        });
      } catch (error) {
        console.error('PayPal capture error:', error);
        onError(
          error instanceof Error
            ? error
            : new Error('Erreur lors de la capture du paiement')
        );
      }
    },
    [onSuccess, onError, premiumPriceString]
  );

  /**
   * Handle payment cancellation
   * @validates Requirement 5.1
   */
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  /**
   * Handle PayPal errors
   * @validates Requirement 5.2
   */
  const handleError = useCallback(
    (error: Error) => {
      console.error('PayPal error:', error);
      onError(error);
    },
    [onError]
  );

  /**
   * Render PayPal buttons when enabled and SDK is loaded
   */
  useEffect(() => {
    // Don't render if disabled or no container
    if (!enabled || !containerRef.current) {
      return;
    }

    const paypal = getPayPal();
    if (!paypal) {
      console.error('PayPal SDK not loaded');
      return;
    }

    // Clear container content before rendering new buttons
    // This prevents duplicate buttons in React StrictMode
    const container = containerRef.current;
    container.innerHTML = '';

    // Create PayPal buttons instance
    const buttonsConfig: PayPalButtonsConfig = {
      style: PAYPAL_BUTTON_STYLE,
      createOrder,
      onApprove: handleApprove,
      onCancel: handleCancel,
      onError: handleError,
    };

    let buttonsInstance: PayPalButtonsInstance | null = null;

    try {
      buttonsInstance = paypal.Buttons(buttonsConfig);

      // Render buttons to the container
      if (container && buttonsInstance) {
        buttonsInstance
          .render(container)
          .catch((err: Error) => {
            console.error('PayPal button render error:', err);
            // Don't call onError here as it might be a normal re-render scenario
          });
      }
    } catch (error) {
      console.error('PayPal Buttons initialization error:', error);
    }

    // Cleanup function
    return () => {
      if (buttonsInstance) {
        try {
          buttonsInstance.close();
        } catch {
          // Ignore errors during cleanup
        }
      }
      // Clear container on cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [enabled, createOrder, handleApprove, handleCancel, handleError]);

  // Hide the button when not enabled (terms not accepted)
  // @validates Requirement 7.2, 7.3, 7.5
  if (!enabled) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="w-full min-h-[150px]"
      data-testid="paypal-button-container"
      aria-label="Boutons de paiement PayPal"
    />
  );
}

export default PayPalButton;
